import React, {useState} from 'react';
import {getGrecaptchaToken} from '@Libraries/recaptcha-library';
import {
  AUTH_MODE,
  EMAIL_PLACEHOLDER,
  getCommonAuthFormParams,
  getoAuthFormParams,
  getRecaptchaSelectorValue,
  handleAuthFormSubmit,
  LOGIN_OPTIONS,
  PASSWORD_PLACEHOLDER,
} from '@Libraries/login-signup-library';
import {executeThunk} from '@Utils/thunk.util';
import {submitAuthForm} from '@Components/login-page/login-page-thunk';
import type {AsyncLoginParams, AuthFormSubmissionParams, BaseAuthFormProps, OauthLoginParams, WebLoginParams} from '@Components/login-page/login-page.types';
import {type AsyncLoginSuccessResponse, AuthFormSubmissionType} from '@Components/login-page/login-page.types';
import {useIsLoginPageFieldErrorActive, useLoginPageAuthButtonsDisabled, useLoginPageAuthMode, useLoginPageLoginActionType} from '@Components/login-page/login-page.hooks';
import {isValidEmail} from '@Utils/string.util';
import {INPUT_FIELD_TYPE} from '../input-field';
import {Text} from '../text';
import {ClickableDiv} from '../clickable-div';
import {ButtonType} from '@Components/button';
import {AuthInput} from '../auth-input';
import styles from './login-form.module.scss';
import {AuthFormSubmitButton} from '../auth-form-submit-button';
import {useAppDispatch} from '@/hooks';
import {useLoginError} from '@/hooks/login-signup/useLoginError';
import {useOpenTwoFactorVerification} from '@Hooks/login-signup/useOpenTwoFactorVerification';
import {noop} from '@Utils/general.util';
import useProcessAsyncLoginSuccessResponse from '@Hooks/login-signup/useProcessAsyncLoginSuccessResponse';
import {clearFieldErrorMarks, setInactiveUserPasswordResetSniperLink, updateForgotPasswordEmail} from '@Components/login-page/login-page-reducer';
import {openInactiveUserPasswordResetModal} from "@Modals/inactive-user-password-reset-modal/inactive-user-password-reset-modal-library";
import {closeModal} from "@Components/modal-container/modal-container-reducer";
import {MODALS_IDS} from "@Components/modal-container";
import {useOnRecaptchaRender} from "@Hooks/recaptcha/useOnRecaptchaRender";
import {AuthTestId} from '@/test-ids/auth-test-id';

interface LoginFormProps extends BaseAuthFormProps {
  switchToForgotPassword?: VoidFunction;
  loginOptionsMode?: LOGIN_OPTIONS;
  onPanelUpdate?: VoidFunction;
  switchToInactivePasswordReset?: VoidFunction;
  /** A remembered account's email (§32): shown read-only, so the form asks for the password alone. */
  lockedEmail?: string;
}

const LOGIN_RECAPTCHA_SELECTOR = 'js-g-recaptcha-response-login';

export function LoginForm({
  switchToForgotPassword = $.noop,
  showSmallButton = false,
  loginOptionsMode = LOGIN_OPTIONS.DEFAULT,
  onAuthFormSubmitSuccess = $.noop,
  onPanelUpdate = $.noop,
  switchToInactivePasswordReset = $.noop,
  lockedEmail = '',
}: LoginFormProps) {
  const {onLoginError} = useLoginError();
  const {openTwoFactorVerification} = useOpenTwoFactorVerification();
  const disableLoginButton = useLoginPageAuthButtonsDisabled();
  const authMode = useLoginPageAuthMode();
  const loginActionType = useLoginPageLoginActionType();
  const isPageErrorActive = useIsLoginPageFieldErrorActive();
  const {processAsyncLoginSuccessResponse} = useProcessAsyncLoginSuccessResponse();
  const {hideGrecaptchaBadge} = useOnRecaptchaRender();
  const dispatch = useAppDispatch();

  const [email, setEmail] = useState(lockedEmail);
  const isEmailLocked = lockedEmail !== '';
  const [password, setPassword] = useState('');
  const [isEmailInvalid, setIsEmailInvalid] = useState(false);
  const [isPasswordInvalid, setIsPasswordInvalid] = useState(false);

  const isLoginEmailInvalid = (): boolean => {
    return !isValidEmail(email);
  };

  const isLoginPasswordInvalid = (): boolean => {
    return password.length === 0;
  };

  const isSubmitDisabled = (): boolean => {
    return disableLoginButton || (isEmailLocked && isLoginPasswordInvalid());
  };

  const onSubmit = () => {
    const isEmailInvalid = isLoginEmailInvalid();
    const isPasswordInvalid = isLoginPasswordInvalid();

    setIsEmailInvalid(isEmailInvalid);
    setIsPasswordInvalid(isPasswordInvalid);

    if (isEmailInvalid || isPasswordInvalid) {
      return;
    }

    if (window.PMW.grecaptchaReady) {
      getGrecaptchaToken(handleAuthFormSubmit.bind(null, onLoginSubmit), LOGIN_RECAPTCHA_SELECTOR);
    }
  };

  /** Focusing or editing either credential lifts the red borders of the last failure (designer call, 2026-10-07); the banner stays until dismissed. */
  const liftFieldErrorMarks = React.useCallback((): void => {
    dispatch(clearFieldErrorMarks());
  }, []);

  const handleEmailInputChange = React.useCallback((txt: string) => {
    setEmail(txt.trim());
    liftFieldErrorMarks();
  }, []);

  const handlePasswordInputChange = React.useCallback((txt: string) => {
    setPassword(txt.trim());
    liftFieldErrorMarks();
  }, []);

  const onLoginSubmit = async () => {
    switch (authMode) {
      case AUTH_MODE.ASYNC:
        await onAsyncLoginSubmit();
        break;
      case AUTH_MODE.DEFAULT:
        await onWebLoginSubmit();
        break;
    }
  };

  const onAsyncLoginSuccess = (response: AsyncLoginSuccessResponse): void => {
    if (response.inactiveUserPasswordResetNeeded) {
      dispatch(setInactiveUserPasswordResetSniperLink(response.inactiveUserPasswordResetSniperLink ?? ''));
      dispatch(closeModal(MODALS_IDS.AUTHENTICATE_MODAL));
      hideGrecaptchaBadge();
      openInactiveUserPasswordResetModal({});
      return;
    }
    processAsyncLoginSuccessResponse(response, onAuthFormSubmitSuccess);
  };

  const onAsyncLoginError = (response: any): void => {
    onLoginError(response.payload.data.reason, onPanelUpdate, email, password);
  };

  const onWebLoginSubmit = async () => {
    const authFormSubmissionParams: AuthFormSubmissionParams = {
      type: AuthFormSubmissionType.WEBLOGIN,
      requestParams: getWebLoginParams(),
    };

    if (loginOptionsMode === LOGIN_OPTIONS.OAUTH) {
      authFormSubmissionParams.type = AuthFormSubmissionType.OAUTHLOGIN;
      authFormSubmissionParams.requestParams = getOauthLoginParams();
    }
    await executeThunk(
      () => {
        return dispatch(submitAuthForm(authFormSubmissionParams));
      },
      onWebLoginSuccess,
      noop
    );
  };

  const onWebLoginSuccess = (response: any): void => {
    if ('pendingMFA' in response && response.pendingMFA) {
      openTwoFactorVerification();
    }

    if ('inactiveUserPasswordResetNeeded' in response && response.inactiveUserPasswordResetNeeded) {
      switchToInactivePasswordReset();
    }
  };

  const onAsyncLoginSubmit = async () => {
    const authFormSubmissionParams: AuthFormSubmissionParams = {
      type: AuthFormSubmissionType.ASYNCLOGIN,
      requestParams: getAsyncLoginParams(),
    };
    await executeThunk(
      () => {
        return window.PMW.redux.store.dispatch(submitAuthForm(authFormSubmissionParams));
      },
      onAsyncLoginSuccess,
      onAsyncLoginError
    );
  };

  const getWebLoginParams = () => {
    const webLoginParams: WebLoginParams = {
      email,
      password,
      gRecaptchaResponse: getRecaptchaSelectorValue(),
      studentlogin: 0,
      ...getCommonAuthFormParams(),
    };

    return webLoginParams;
  };

  const getOauthLoginParams = () => {
    const oauthLoginParams: OauthLoginParams = {
      email,
      password,
      ...getoAuthFormParams(),
    };
    return oauthLoginParams;
  };

  const getAsyncLoginParams = () => {
    const asyncLoginParams: AsyncLoginParams = {
      email,
      password,
      gRecaptchaResponse: getRecaptchaSelectorValue(),
      isRecaptchaV2Confirmation: 0,
    };
    return asyncLoginParams;
  };

  /** Hands the typed address to the forgot-password panel, which remounts and would otherwise start empty. */
  const onForgotPasswordClick = (): void => {
    dispatch(updateForgotPasswordEmail(email));
    switchToForgotPassword();
  };

  const onEmailFocusOut = (): void => {
    if (email === '') {
      return;
    }
    setIsEmailInvalid(isLoginEmailInvalid());
  };

  const onPasswordFocusOut = (): void => {
    setIsPasswordInvalid(isLoginPasswordInvalid());
  };

  return (
    <>
      <input type="hidden" name="gRecaptchaResponse" className={`g-recaptcha-response ${LOGIN_RECAPTCHA_SELECTOR}`} data-recaptcha-action={loginActionType} />

      <AuthInput
        data-testid={AuthTestId.EMAIL_INPUT}
        disabled={isEmailLocked}
        enableFocusStyles
        errorTestId={AuthTestId.EMAIL_ERROR}
        label={window.i18next.t('pmwjs_email')}
        placeholder={EMAIL_PLACEHOLDER}
        type={INPUT_FIELD_TYPE.EMAIL}
        value={lockedEmail}
        handleInput={handleEmailInputChange}
        showError={isEmailInvalid}
        onInputSubmit={onSubmit}
        errorText={window.i18next.t('pmwjs_valid_email_address')}
        onFocusCallback={liftFieldErrorMarks}
        onFocusOutCallback={onEmailFocusOut}
      />

      <div className={styles.passwordContainer}>
        <AuthInput
          data-testid={AuthTestId.PASSWORD_INPUT}
          enableFocusStyles
          errorTestId={AuthTestId.PASSWORD_ERROR}
          iconTestId={AuthTestId.PASSWORD_TOGGLE}
          label={window.i18next.t('pmwjs_password')}
          type={INPUT_FIELD_TYPE.PASSWORD}
          placeholder={PASSWORD_PLACEHOLDER}
          onFocusCallback={liftFieldErrorMarks}
          onFocusOutCallback={onPasswordFocusOut}
          handleInput={handlePasswordInputChange}
          showError={isPasswordInvalid}
          isInvalid={isPageErrorActive}
          onInputSubmit={onSubmit}
          errorText={window.i18next.t('pmwjs_please_enter_a_valid_password')}
        />
      </div>

      <ClickableDiv className={styles.forgotPassword} onClick={onForgotPasswordClick} data-testid={AuthTestId.FORGOT_PASSWORD_LINK}>
        <Text val={window.i18next.t('pmwjs_login_forgot_password')} className={styles.forgotPasswordLink} />
      </ClickableDiv>

      <AuthFormSubmitButton
        data-testid={AuthTestId.SUBMIT_BUTTON}
        btnText={window.i18next.t('pmwjs_log_in')}
        type={ButtonType.PRIMARY}
        showSmallButton={showSmallButton}
        onClick={onSubmit}
        customClasses="-fullwidth"
        isDisabled={isSubmitDisabled()}
      />
    </>
  );
}
